import styled from "styled-components";

const breakpoints = {
  lg: "992px",
  md: "768px",
  sm: "576px",
};

// Helper function to safely get theme colors without optional chaining
const getThemeColor = (theme, section, key, fallback) => {
  if (theme && theme.colors && theme.colors[section] && theme.colors[section][key]) {
    return theme.colors[section][key];
  }
  return fallback;
};

export const StyledCard = styled.div`
  background: ${({ theme }) => getThemeColor(theme, 'background', 'paper', '#ffffff')};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid ${({ theme }) => getThemeColor(theme, 'border', 'light', '#EAECF0')};
  overflow: hidden;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  padding: 1.5rem;
  margin-bottom: 24px;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: ${breakpoints.lg}) {
    padding: 1.25rem;
  }

  @media (max-width: ${breakpoints.md}) {
    padding: 1rem;
    border-radius: 10px;
    margin-bottom: 16px;
  }

  @media (max-width: ${breakpoints.sm}) {
    padding: 0.85rem;
    box-shadow: none;
    border-radius: 8px;
    margin-bottom: 12px;
  }
`;

export const StyledCardHeader = styled.div`
  padding-bottom: 12px;
  margin-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => getThemeColor(theme, 'border', 'light', '#EAECF0')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;

  h3, h4, h5 {
    margin: 0;
    font-weight: 600;
    color: ${({ theme }) => getThemeColor(theme, 'text', 'primary', '#101828')};
  }
  
  && i.fas {
    color: ${({ theme }) => getThemeColor(theme, 'brand', 'primary', '#0088cc')};
    font-size: 1.3rem;
  }

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

export const ScreenContainer = styled.div`
  background: #F2F4F7;
  min-height: 100vh;
  padding: 24px;
  box-sizing: border-box;
  width: 100%;

  @media (max-width: ${breakpoints.md}) {
    padding: 0;
    min-height: auto;
    background: transparent;
  }
`;

export const ScreenContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

export const HeroHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 16px;
  }
`;

export const HeroHeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-width: 0;

  div {
    min-width: 0;
    word-break: break-word;
  }

  @media (max-width: ${breakpoints.sm}) {
    gap: 12px;
  }
`;

export const HeroIcon = styled.div`
  background: #0088cc;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.5rem;
  flex-shrink: 0;

  @media (max-width: ${breakpoints.sm}) {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    font-size: 1.1rem;
  }
`;

export const StepperWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 0;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

export const StepperItem = styled.div`
  flex: 1;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid ${({ active }) => (active ? '#0088cc' : '#EAECF0')};
  background: ${({ active }) => (active ? '#eff8ff' : '#F9FAFB')};
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  box-sizing: border-box;

  strong {
    word-break: break-word;
    font-size: 0.95rem;
  }

  @media (max-width: ${breakpoints.md}) {
    width: 100%;
    padding: 10px 12px;
  }
`;

export const StepperMarker = styled.div`
  background: ${({ active }) => (active ? '#0088cc' : '#D0D5DD')};
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  flex-shrink: 0;
`;

export const FooterActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column-reverse;
    gap: 12px;
    margin-top: 16px;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ theme }) => getThemeColor(theme, 'text', 'primary', '#101828')};
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.md}) {
    margin-bottom: 0.75rem;
    font-size: 1.1rem;
  }
`;

export const StyledInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 1rem;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;

  label {
    font-weight: 500;
    font-size: 0.875rem;
    color: ${({ theme }) => getThemeColor(theme, 'text', 'secondary', '#475467')};
    word-break: break-word;
  }

  input, select, textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 0.625rem 0.875rem;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => getThemeColor(theme, 'border', 'main', '#D0D5DD')};
    font-size: 0.95rem;
    outline: none;
    transition: all 0.2s;
    background: ${({ theme }) => getThemeColor(theme, 'background', 'main', '#FFFFFF')};
    color: ${({ theme }) => getThemeColor(theme, 'text', 'primary', '#101828')};

    &:focus {
      border-color: ${({ theme }) => getThemeColor(theme, 'brand', 'primary', '#0088cc')};
      box-shadow: 0 0 0 3px rgba(0, 136, 204, 0.15);
    }
    
    &:disabled {
      background: #F2F4F7;
      cursor: not-allowed;
    }
  }
`;

export const PillContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  background: ${({ theme }) => getThemeColor(theme, 'background', 'soft', '#F9FAFB')};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => getThemeColor(theme, 'border', 'light', '#EAECF0')};
  flex: 1;
  min-width: 0;
  box-sizing: border-box;

  span.label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: ${({ theme }) => getThemeColor(theme, 'text', 'tertiary', '#667085')};
    margin-bottom: 4px;
    letter-spacing: 0.05em;
  }

  span.value {
    font-size: 1rem;
    font-weight: 600;
    color: ${({ theme }) => getThemeColor(theme, 'text', 'primary', '#101828')};
    word-break: break-word;
  }

  @media (max-width: ${breakpoints.md}) {
    flex: 1 1 100% !important;
    padding: 10px 12px;
  }
`;

export const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

export const ServiceValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 8px;

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

export const RpsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  background: #fff;
  border: 1px solid #EAECF0;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 12px;
    margin-bottom: 16px;
  }
`;

export const RpsInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
  }
`;

export const RpsDataRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.md}) {
    gap: 12px;
    width: 100%;
  }
`;

export const ActionButton = styled.button`
  background: ${({ variant, theme }) => 
    variant === 'primary' ? getThemeColor(theme, 'brand', 'primary', '#0088cc') :
    variant === 'danger' ? getThemeColor(theme, 'feedback', 'error', '#D92D20') :
    "transparent"};
  color: ${({ variant, theme }) => 
    variant === 'primary' || variant === 'danger' ? "#ffffff" : 
    getThemeColor(theme, 'text', 'primary', '#101828')};
  border: 1px solid ${({ variant, theme }) => 
    variant === 'secondary' ? getThemeColor(theme, 'border', 'main', '#D0D5DD') : "transparent"};
  border-radius: 8px;
  padding: 0.625rem 1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  box-sizing: border-box;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.55 !important;
    cursor: not-allowed !important;
    pointer-events: none !important;
    transform: none !important;
    box-shadow: none !important;
  }

  @media (max-width: ${breakpoints.md}) {
    width: ${({ $stackedOnMobile }) => ($stackedOnMobile ? '100%' : 'auto')};
    font-size: 0.95rem;
    padding: 0.6rem 0.9rem;
  }

  i {
    font-size: 1rem;
  }
`;

export const TableWrapper = styled.div`
  border-radius: 8px;
  border: 1px solid ${({ theme }) => getThemeColor(theme, 'border', 'light', '#EAECF0')};
  overflow: hidden;
  margin-top: 1rem;
  background: ${({ theme }) => getThemeColor(theme, 'background', 'paper', '#ffffff')};
  width: 100%;
  overflow-x: auto;
  box-sizing: border-box;

  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 720px;
    
    th {
      background: ${({ theme }) => getThemeColor(theme, 'background', 'soft', '#F9FAFB')};
      padding: 12px 16px;
      text-align: left;
      font-size: 0.825rem;
      font-weight: 600;
      color: ${({ theme }) => getThemeColor(theme, 'text', 'secondary', '#475467')};
      border-bottom: 2px solid ${({ theme }) => getThemeColor(theme, 'border', 'light', '#EAECF0')};
    }

    td {
      padding: 16px;
      font-size: 0.95rem;
      color: ${({ theme }) => getThemeColor(theme, 'text', 'primary', '#101828')};
      border-bottom: 1px solid ${({ theme }) => getThemeColor(theme, 'border', 'light', '#EAECF0')};
      vertical-align: middle;
    }

    tr:last-child td {
      border-bottom: none;
    }
    
    tr:hover td {
      background: #F9FAFB;
    }
  }

  @media (max-width: ${breakpoints.md}) {
    border-radius: 12px;
  }

  @media (max-width: ${breakpoints.sm}) {
    border: none;
    padding-bottom: 8px;
    table {
      min-width: 600px;
    }
  }
`;

export const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  @media (min-width: ${breakpoints.md}) {
    display: none;
  }
`;

export const CardListItem = styled.div`
  border-radius: 12px;
  border: 1px solid ${({ theme }) => getThemeColor(theme, 'border', 'light', '#EAECF0')};
  padding: 16px;
  background: ${({ theme }) => getThemeColor(theme, 'background', 'paper', '#ffffff')};
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 8px 20px rgba(16, 24, 40, 0.05);
`;

export const CardListItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

export const CardListItemActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
`;

export const HelperButton = styled.button`
  border: 1px solid #0088cc;
  border-radius: 6px;
  background: transparent;
  color: #0088cc;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 6px 10px;
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 136, 204, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: ${breakpoints.md}) {
    width: 100%;
    justify-content: center;
  }
`;

export const DesktopOnly = styled.div`
  @media (max-width: ${breakpoints.md}) {
    display: none !important;
  }
`;

export const MobileOnly = styled.div`
  @media (min-width: ${breakpoints.md}) {
    display: none !important;
  }
`;



// WEBPACK FOOTER //
// ./src/views/Fiscal/NFSe/emit/components/styles.jsx